import i18n from "@/src/i18n";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import { DateTime } from "luxon";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const latitudeRegex =
    /^(\+|-)?(?:90(?:(?:\.0{1,7})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,7})?))$/;
const longitudeRegex =
    /^(\+|-)?(?:180(?:(?:\.0{1,7})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,7})?))$/;

export const StationTypeEnumSchema = z.enum([
    "Agronic",
    "Aqua",
    "Box Counter",
    "Camera",
    "CameraMeteo",
    "Geo",
    "Gps",
    "Handheld",
    "ElectricMeter",
    "MeteoMobile",
    "Meteo",
    "Track",
    "Trap",
]);

export type StationTypeEnum = z.output<typeof StationTypeEnumSchema>;

export const PEST_TAB_HIDDEN_STATION_TYPES: readonly StationTypeEnum[] = [
    StationTypeEnumSchema.enum.Aqua,
    StationTypeEnumSchema.enum.Gps,
];

// Same exclusions as the pest tab (IOT-1776)
export const RELATED_CHARTS_TAB_HIDDEN_STATION_TYPES: readonly StationTypeEnum[] =
    PEST_TAB_HIDDEN_STATION_TYPES;

export const CAMPAIGN_EXCLUDED_STATION_TYPES: readonly StationTypeEnum[] = [
    StationTypeEnumSchema.enum.Aqua,
    StationTypeEnumSchema.enum.Geo,
    StationTypeEnumSchema.enum.Gps,
];

const DecisionSupportSystemsSchema = z
    .object({
        id: z.number(),
        configuration: z.object({
            id: z.number(),
            type: z.string().trim(),
            name: z.string().trim(),
            description: z.string().trim(),
        }),
    })
    .readonly();

const PoiSchema = z
    .object({
        id: z.number(),
        code: z.string().trim(),
        name: z.string().trim(),
        technicalName: z.string().trim(),
        type: z.string().trim(),
    })
    .readonly();
export type Poi = z.output<typeof PoiSchema>;

const CampaignSchema = z
    .object({
        id: z.number(),
        name: z.string().trim(),
        cropType: z.object({
            id: z.number(),
            name: z.string().trim(),
        }),
    })
    .readonly();
export type Campaign = z.output<typeof CampaignSchema>;

const StationSchema = z
    .object({
        id: zIdSchema,
        code: z.string().trim(),
        name: z.string().trim().nullable(),
        technicalName: z.string().trim(),
        latitude: z.string().trim(),
        longitude: z.string().trim(),
        radius: z.number(),
        type: StationTypeEnumSchema.nullable(),
        oldThresholdHours: z.number().nullable(),
        clientCompany: z.string().trim(),
        icon: z.string().trim(),
        lastFrameAt: z.string().trim(),
        lastFrameTimestamp: z.string().trim(),
        vehicleType: z.string().trim(),
        allowDrainageReport: z.boolean().nullable(),
        decisionSupportSystems: z.array(DecisionSupportSystemsSchema),
        pois: z.array(PoiSchema),
        batteryLevel: z.number().nullable(),
        campaign: CampaignSchema.nullable(),
    })
    .brand<"Station">();

export type StationInputProps = z.input<typeof StationSchema>;
export type StationProps = z.output<typeof StationSchema>;

export class Station {
    private static readonly DEFAULT_OLD_THRESHOLD_HOURS = 2;

    private constructor(private readonly props: Readonly<StationProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get code(): string {
        return this.props.code;
    }

    get name(): Nullable<string> {
        return this.props.name;
    }

    get displayName(): string {
        return this.props.name ?? "-";
    }

    get technicalName(): string | null {
        return this.props.technicalName;
    }

    get latitude(): string {
        return this.props.latitude;
    }

    get longitude(): string {
        return this.props.longitude;
    }

    get type(): Nullable<StationTypeEnum> {
        return this.props.type;
    }

    // Unknown/missing type falls back to showing the tab (IOT-1769)
    get showsPestTab(): boolean {
        return (
            !this.props.type ||
            !PEST_TAB_HIDDEN_STATION_TYPES.includes(this.props.type)
        );
    }

    // Unknown/missing type falls back to showing the tab (IOT-1776)
    get showsRelatedChartsTab(): boolean {
        return (
            !this.props.type ||
            !RELATED_CHARTS_TAB_HIDDEN_STATION_TYPES.includes(this.props.type)
        );
    }

    // Unknown/missing type falls back to selectable (IOT-1769)
    get isCampaignSelectable(): boolean {
        return (
            !this.props.type ||
            !CAMPAIGN_EXCLUDED_STATION_TYPES.includes(this.props.type)
        );
    }

    get oldThresholdHours(): number | null {
        return this.props.oldThresholdHours;
    }

    get clientCompany(): string | null {
        return this.props.clientCompany;
    }

    get icon(): string {
        return this.props.icon;
    }

    get lastFrameAt(): string | null {
        return this.props.lastFrameAt;
    }

    get lastFrameTimestamp(): string | null {
        return this.props.lastFrameTimestamp;
    }

    get vehicleType(): string | null {
        return this.props.vehicleType;
    }

    get allowDrainageReport(): boolean | null {
        return this.props.allowDrainageReport;
    }

    get decisionSupportSystems(): z.output<
        typeof DecisionSupportSystemsSchema
    >[] {
        return this.props.decisionSupportSystems;
    }

    get pois(): Poi[] {
        return this.props.pois;
    }

    get batteryLevel(): Nullable<number> {
        return this.props.batteryLevel ?? null;
    }

    get campaign(): Nullable<Campaign> {
        return this.props.campaign ?? null;
    }

    get latestDataTimestamp(): Nullable<number> {
        return this.props.lastFrameTimestamp
            ? DateTime.fromISO(this.props.lastFrameTimestamp).toSeconds()
            : null;
    }

    get oldThresholdHoursTimestamp(): number {
        return DateTime.now()
            .minus({
                hours:
                    this.props.oldThresholdHours ??
                    Station.DEFAULT_OLD_THRESHOLD_HOURS,
            })
            .toSeconds();
    }

    static validate(
        props: Readonly<MaybeValidProps<StationInputProps>>,
    ): Result<StationProps, z.ZodError<StationProps>> {
        const result = StationSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<StationInputProps>>,
    ): Result<StationProps, PartialValid<typeof Station>> {
        const result = partialSafeParse(StationSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<StationInputProps>,
    ): Result<Station, z.ZodError<StationProps>> {
        return Station.validate(props).map(
            (validatedProps) => new Station(validatedProps),
        );
    }

    static createWithRecovery(
        props: StationInputProps,
    ): Result<Station, ModelError<typeof Station>> {
        return Station.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<StationProps> = {
                    id: error.validProps.id,
                    code: "",
                    name: null,
                    technicalName: "",
                    latitude: "",
                    longitude: "",
                    radius: 0,
                    type: "Agronic",
                    oldThresholdHours: 2,
                    clientCompany: "",
                    icon: "",
                    lastFrameAt: "",
                    lastFrameTimestamp: "",
                    vehicleType: "",
                    allowDrainageReport: false,
                    decisionSupportSystems: [],
                    pois: [],
                    batteryLevel: null,
                    campaign: null,
                };

                const recoveryProps: Unbrand<StationProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Station>(
                    new Station(recoveryProps as StationProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Station(validatedProps));
    }

    refreshLastFrame(
        lastFrameAt: DateTime<true>,
    ): Result<Station, FallbackError> {
        return Station.create({
            ...this.toCreateProps(),
            lastFrameAt: lastFrameAt.toISO(),
            lastFrameTimestamp: lastFrameAt.toISO(),
        }).mapErr(() => new FallbackError());
    }

    toCreateProps(): StationInputProps {
        return {
            id: this.props.id.toCreateProps(),
            code: this.props.code,
            name: this.props.name,
            technicalName: this.props.technicalName,
            latitude: this.props.latitude,
            longitude: this.props.longitude,
            radius: this.props.radius,
            type: this.props.type,
            oldThresholdHours: this.props.oldThresholdHours,
            clientCompany: this.props.clientCompany,
            icon: this.props.icon,
            lastFrameAt: this.props.lastFrameAt,
            lastFrameTimestamp: this.props.lastFrameTimestamp,
            vehicleType: this.props.vehicleType,
            allowDrainageReport: this.props.allowDrainageReport,
            decisionSupportSystems: this.props.decisionSupportSystems,
            pois: this.props.pois,
            batteryLevel: this.props.batteryLevel,
            campaign: this.props.campaign,
        };
    }
}

const StationsSchema = z
    .object({
        stations: z.array(
            z.custom<Station>((val) => val instanceof Station, {
                error: "Invalid Station instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"Stations">();

export type StationsInputProps = z.input<typeof StationsSchema>;
export type StationsProps = z.output<typeof StationsSchema>;

export class Stations {
    private constructor(private readonly props: Readonly<StationsProps>) {}

    get stations(): readonly Readonly<Station>[] {
        return this.props.stations;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): Stations {
        return Stations.create({
            stations: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<StationsInputProps>>,
    ): Result<StationsProps, z.ZodError<StationsProps>> {
        const result = StationsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<StationsInputProps>,
    ): Result<Stations, z.ZodError<StationsProps>> {
        return Stations.validate(props).map(
            (validatedProps) => new Stations(validatedProps),
        );
    }
}

export const StationUpdateSchema = z
    .object({
        id: z.number().positive(),
        name: z.string().trim().min(1),
        technicalName: z.string().trim().nullable(),
        latitude: z
            .string()
            .trim()
            .nullable()
            .refine((lat) => !lat || latitudeRegex.test(lat), {
                error: () =>
                    i18n.t("validation:models.station.invalidLatitude"),
            }),
        longitude: z
            .string()
            .trim()
            .nullable()
            .refine((lon) => !lon || longitudeRegex.test(lon), {
                error: () =>
                    i18n.t("validation:models.station.invalidLongitude"),
            }),
    })
    .superRefine((station, ctx) => {
        const hasLatitude = !!station.latitude;
        const hasLongitude = !!station.longitude;

        if (hasLatitude && !hasLongitude) {
            ctx.addIssue({
                path: ["longitude"],
                code: "custom",
                message: i18n.t("validation:models.station.longitudeRequired"),
            });
        }

        if (!hasLatitude && hasLongitude) {
            ctx.addIssue({
                path: ["latitude"],
                code: "custom",
                message: i18n.t("validation:models.station.latitudeRequired"),
            });
        }
    });
export type StationUpdate = z.output<typeof StationUpdateSchema>;
export interface StationListSearchFilters {
    search: string;
}
